import React from 'react';
import {Container} from '@inlet/react-pixi';
import {GlobalEventsMapForBackwardCompatibility} from '@gongxigames/framework/slot/events';
import {CoreDI, CurrencyFormatter, DeviceType, inject, injectable, OffsetPosition, OffsetPositionByOrientation} from '@gongxigames/framework/core';
import {FlexBoxComponent, Rectangle, RichText} from '@gongxigames/framework/core/fragments/react';
import {ReactView} from '@gongxigames/framework/slot/fragments/module/view/ReactView';
import {LAYERS} from '@gongxigames/framework/slot/configs/Layers';
import {DI} from '@gongxigames/framework/slot/configs/DI';
import {GameConfig, ILogic} from '@gongxigames/framework/slot/types';
import {counterOffsets} from '@gongxigames/framework/slot/modules/promotionsCounter/counterOffsets';
import {CounterData} from '@gongxigames/framework/promotions';

type PromotionCounterViewState = CounterData & {
    visible: boolean;
};

@injectable()
export class PromotionCounterView extends ReactView<PromotionCounterViewState, GlobalEventsMapForBackwardCompatibility> {
    public static readonly NAME: symbol = Symbol('PromotionCounterView');
    public static layer = LAYERS.SYSTEM_POPUPS;

    @inject(DI.Logic)
    protected _logic!: ILogic;

    @inject(CoreDI.CurrencyFormatter)
    protected _currencyFormatter!: CurrencyFormatter;

    @inject(CoreDI.GameConfig)
    protected _gameConfig!: GameConfig;

    public state: PromotionCounterViewState = {
        visible: false,
        left: 0,
        valuedAtEnabled: true,
        type: 'free-round'
    };

    public showCounter(): void {
        this.setState({visible: true});
    }

    public hideCounter(): void {
        this.setState({visible: false});
    }

    public updateCounter(data: CounterData): void {
        this.setState({...data});
    };

    public onOrientationChanged(): void {
        super.onOrientationChanged();

        this.forceUpdate();
    }

    protected getOffsetPosition(): Required<OffsetPosition> {
        const currentPlatform = this._settings.get('platform');
        const orientation = this._screenManager.isLandscapeOrientation() ? 'landscape' : 'portrait';
        const isDesktop = currentPlatform === DeviceType.DESKTOP;
        const promotionViewConfig = this._gameConfig.freeRoundsViewConfig;
        const offsetByPlatform = promotionViewConfig?.counterOffsetPosition?.[currentPlatform];
        const initialOffsets = {x: 0, y: 0};
        let frameworkOffsets;
        let gameSpecificOffsets;

        if (isDesktop) {
            frameworkOffsets = counterOffsets[currentPlatform];
            gameSpecificOffsets = offsetByPlatform;
        } else {
            frameworkOffsets = (counterOffsets[currentPlatform] as OffsetPositionByOrientation)[orientation];
            gameSpecificOffsets = (offsetByPlatform as OffsetPositionByOrientation)?.[orientation];
        }

        return Object.assign(initialOffsets, frameworkOffsets, gameSpecificOffsets);
    }

    public render(): JSX.Element {
        const {visible, left, id, bet, price, valuedAtEnabled, type} = this.state;
        const {x, y} = this.getOffsetPosition();

        const roundValue = type === 'free-round' ? bet : price;
        const titlesStyle = left === 0 ? 'PromotionTitleAndTitle' : 'PromotionCounterTitle';
        const titlesTextLastRounds = type === 'free-round' ? 'lastFreeRound' : 'lastAwardedFeature';
        const titlesTextOtherRounds = type === 'free-round' ? 'relaxFreeRoundsCounterTitle' : 'awardedFeatureCounterTitle';
        const titlesText = left === 0 ? titlesTextLastRounds : titlesTextOtherRounds;
        const counterWith4Rows = id && valuedAtEnabled;
        const counterText = 'freeGamesCounterLeftAmountRichText';
        const counterStyle = counterWith4Rows ? 'PromotionCounterAmount4Rows' : 'PromotionCounterAmount3Rows';
        const valuedAtText = counterWith4Rows 
                                    ? 'relaxFreeRoundsBetRichText4Rows' 
                                    : left === 0 
                                        ? 'relaxFreeRoundsBetRichText4Rows' 
                                        : 'relaxFreeRoundsBetRichText';
        const valuedAtStyle = counterWith4Rows ? 'PromotionCounterBet4Rows' : 'PromotionCounterBet3Rows';
        const voucherText = 'freeRoundVoucher';
        const voucherStyle = 'PromotionCounterId';

        const roundsCounterPresent = left > 0;
        const valuedAtPresent = bet && valuedAtEnabled;
        const voucherPresent = id;
        let gap = 25;
        gap = roundsCounterPresent ? gap - 20 : gap; 
        gap = voucherPresent ? gap - 5 : gap; 

        return (
            <Container x={x} y={y} visible={visible} name={PromotionCounterView.NAME.toString()}>
                <Rectangle width={340} height={430} round={50} fill={0x000000}/>
                <FlexBoxComponent containerWidth={340} containerHeight={430} justifyContent={'center'} alignItems={'center'} gap={gap} direction={'column'} >
                    <Container>
                        <RichText style={titlesStyle} id={titlesText} maxWidth={330}/>
                    </Container>
                    {roundsCounterPresent && <Container>
                        <RichText style={counterStyle} id={counterText} values={{left}} maxWidth={330}/>
                    </Container>}
                    {valuedAtPresent && <Container>
                        <RichText style={valuedAtStyle} id={valuedAtText} values={{bet: this._currencyFormatter.formatCents(roundValue ?? 0)}} maxWidth={330}/>
                    </Container>}
                    {voucherPresent && <Container>
                        <RichText style={voucherStyle} id={voucherText} values={{id}} maxWidth={330}/>
                    </Container>}
                </FlexBoxComponent>
            </Container>
        );
    }
}
